import React from 'react';
import { 
  X, 
  CheckCircle2, 
  Scale, 
  ShieldCheck, 
  Briefcase, 
  Award, 
  Calendar, 
  ArrowRight, 
  UserCheck, 
  FileText, 
  Clock, 
  PhoneCall 
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { localizeLawyerPosition } from '../../utils/helpers';

export default function PracticeAreaCatalogModal({ isOpen, onClose, topic, onBookTopic, onBookLawyer, lawyers = [] }) {
  const { t, currentLang, localized } = useLanguage();

  if (!isOpen || !topic) return null;

  // Localized topic values
  const topicTitle = currentLang === 'uz' 
    ? (topic.titleUz || topic.title) 
    : currentLang === 'ru' 
    ? (topic.titleRu || topic.titleUz || topic.title) 
    : (topic.titleEn || topic.title);

  const topicDesc = currentLang === 'uz'
    ? (topic.descUz || topic.description)
    : currentLang === 'ru'
    ? (topic.descRu || topic.descUz || topic.description)
    : (topic.descEn || topic.description);

  const topicItems = currentLang === 'uz'
    ? (topic.itemsUz || topic.items)
    : currentLang === 'ru'
    ? (topic.itemsRu || topic.itemsUz || topic.items)
    : (topic.itemsEn || topic.items);

  // Find relevant lawyers for this topic
  const topicLawyers = lawyers.filter(l => 
    l.practiceAreas?.some(p => p.toLowerCase().includes(topic.title?.toLowerCase() || '')) ||
    l.services?.some(s => s.service?.titleUz?.toLowerCase().includes(topic.title?.toLowerCase() || ''))
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2.5 sm:p-6 overflow-y-auto">
      {/* Dark backdrop with blur */}
      <div 
        className="fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Luxury Dialog Box */}
      <div className="relative w-full max-w-4xl bg-gradient-to-b from-[#0c1322] via-[#090e18] to-[#060a12] border border-[#c59b27]/40 rounded-2xl shadow-2xl shadow-black/90 overflow-hidden z-10 my-auto max-h-[92vh] flex flex-col animate-in zoom-in-95 duration-200 text-slate-200">
        
        {/* Top Glowing Header Accent */}
        <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#c59b27] to-transparent shrink-0" />

        {/* Modal Top Bar */}
        <div className="flex items-center justify-between px-4 sm:px-8 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-[#1b2b46]/60 shrink-0">
          <div className="flex items-center space-x-2.5 sm:space-x-3 min-w-0 mr-2">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#142033] border border-[#c59b27]/40 flex items-center justify-center text-[#c59b27] shrink-0">
              <Scale className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#c59b27] font-semibold block truncate">
                {t('practice_catalog_title', 'Yuridik Amaliyot Katalogi')}
              </span>
              <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-bold text-slate-100 truncate">
                {topicTitle}
              </h2>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#121c2e] hover:bg-[#1a2942] border border-slate-700/60 flex items-center justify-center text-slate-400 hover:text-white transition-colors shrink-0 cursor-pointer"
            aria-label={t('close_btn', 'Yopish')}
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-8 space-y-6 sm:space-y-8 flex-1 overflow-y-auto">
          {/* Main Description */}
          <div className="bg-[#0b101c]/80 border border-[#1b2b46] rounded-xl p-4 sm:p-6">
            <h3 className="font-serif text-base sm:text-lg font-semibold text-slate-100 mb-2 flex items-center">
              <ShieldCheck className="w-5 h-5 text-[#c59b27] mr-2 shrink-0" />
              {t('practice_overview', 'Yo‘nalish haqida umumiy ma’lumot')}
            </h3>
            <p className="text-slate-300 text-xs sm:text-sm leading-relaxed font-light">
              {topicDesc || (currentLang === 'ru'
                ? 'В этой сфере ведущие адвокаты нашей коллегии строго защищают права и имущество клиентов. Полный процесс от первичной консультации до исполнения судебных решений находится под надежным контролем.'
                : currentLang === 'en'
                ? "In this practice area, our senior advocates strictly protect clients' rights and assets. The entire process from initial consultation to the execution of court decisions is kept under vigilant control."
                : 'Ushbu sohada oliy toifali advokatlarimiz mijozlarning huquqlari va mol-mulkini ijobiy va professional himoya qiladi. Dastlabki maslahatdan boshlab sud qarorlari ijrosigacha bo‘lgan to‘liq jarayon nazorat ostiga olinadi.')}
            </p>
          </div>

          {/* Sub-services / Catalog Items */}
          <div>
            <h4 className="font-serif text-sm sm:text-base font-semibold text-[#c59b27] uppercase tracking-wider mb-3 sm:mb-4 flex items-center">
              <Briefcase className="w-4 h-4 mr-2 shrink-0" />
              {t('practice_services_offered', 'Katalog bo‘yicha ko‘rsatiladigan aniq xizmatlar')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3.5">
              {(topicItems || (currentLang === 'ru' ? [
                "Участие адвоката в дознании, следствии, допросах и следственных действиях",
                "Стратегия защиты по сложным экономическим и налоговым делам",
                "Защита по обвинениям в превышении должностных полномочий",
                "Полная защита в судах первой инстанции, апелляции и Верховном суде",
                "Ходатайства об изменении меры пресечения и освобождении под залог",
                "Обжалование приговоров и подача кассационных жалоб"
              ] : currentLang === 'en' ? [
                "Defense participation during interrogation, inquiry, and pretrial proceedings",
                "White-collar and tax investigation defense strategy",
                "Allegations of executive overreach and corporate malpractice",
                "Trial litigation across primary, appellate, and Supreme courts",
                "Motions to modify pretrial detention and secure bail",
                "Appeals and cassation challenges against trial verdicts"
              ] : [
                "Dastlabki tergov va sudgacha bo‘lgan bosqichda huquqiy himoya",
                "Birinchi instansiya, apellyatsiya va kassatsiya sudlarida to‘liq vakillik",
                "Bitimlar va shartnomalarni chuqur yuridik ekspertizadan o‘tkazish",
                "Yetkazilgan moddiy va ma’naviy zararni to‘liq undirish strategiyasi",
                "Davlat organlari va nazorat idoralari bilan munosabatlarda himoya",
                "Kompaniya manfaatlari bo‘yicha shoshilinch advokatlik so‘rovlari va aralashuvi"
              ])).map((item, idx) => (
                <div 
                  key={idx}
                  style={{ animationDelay: `${idx * 60}ms` }}
                  className="animate-catalog-card flex items-start space-x-2.5 sm:space-x-3 bg-[#0d1524] p-3 sm:p-3.5 rounded-lg border border-[#1a273e] hover:border-[#c59b27]/60 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 group"
                >
                  <CheckCircle2 className="w-4 h-4 text-[#c59b27] group-hover:scale-110 transition-transform mt-0.5 shrink-0" />
                  <span className="text-xs sm:text-sm text-slate-200 leading-snug">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Key Advantages / Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div 
              style={{ animationDelay: '120ms' }}
              className="animate-catalog-card bg-[#09111e] border border-[#1b2b46] rounded-xl p-3.5 sm:p-4 text-center hover:border-[#c59b27]/70 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#c59b27]/10 transition-all duration-300 group cursor-default"
            >
              <span className="text-xl sm:text-2xl font-serif font-bold text-[#c59b27] group-hover:scale-110 transition-transform block">1,500+</span>
              <span className="text-[11px] sm:text-xs text-slate-400 mt-1 block">{t('stat_success_rate', 'Ko‘rib chiqilgan ishlar va muzokara amaliyoti')}</span>
            </div>
            <div 
              style={{ animationDelay: '200ms' }}
              className="animate-catalog-card bg-[#09111e] border border-[#1b2b46] rounded-xl p-3.5 sm:p-4 text-center hover:border-[#c59b27]/70 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#c59b27]/10 transition-all duration-300 group cursor-default"
            >
              <span className="text-xl sm:text-2xl font-serif font-bold text-[#c59b27] group-hover:scale-110 transition-transform block">
                {currentLang === 'uz' ? '20+ Yil' : currentLang === 'ru' ? '20+ Лет' : '20+ Years'}
              </span>
              <span className="text-[11px] sm:text-xs text-slate-400 mt-1 block">{t('stat_senior_exp', '20+ yillik advokatlik staji')}</span>
            </div>
            <div 
              style={{ animationDelay: '280ms' }}
              className="animate-catalog-card bg-[#09111e] border border-[#1b2b46] rounded-xl p-3.5 sm:p-4 text-center hover:border-[#c59b27]/70 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#c59b27]/10 transition-all duration-300 group cursor-default"
            >
              <span className="text-xl sm:text-2xl font-serif font-bold text-[#c59b27] group-hover:scale-110 transition-transform block">100%</span>
              <span className="text-[11px] sm:text-xs text-slate-400 mt-1 block">{t('stat_confidentiality', 'Maxfiylik va advokatlik siri kafolati')}</span>
            </div>
          </div>

          {/* Assigned Specialists */}
          {topicLawyers.length > 0 && (
            <div>
              <h4 className="font-serif text-sm sm:text-base font-semibold text-slate-200 mb-3 sm:mb-4 flex items-center">
                <UserCheck className="w-4 h-4 text-[#c59b27] mr-2 shrink-0" />
                {t('lead_attorneys', 'Ushbu soha bo‘yicha mas’ul bosh advokatlar')}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {topicLawyers.map((lawyer, lIdx) => (
                  <div 
                    key={lawyer.id} 
                    style={{ animationDelay: `${320 + lIdx * 80}ms` }}
                    onClick={() => {
                      if (onBookLawyer) {
                        onBookLawyer(lawyer);
                      } else if (onBookTopic) {
                        onBookTopic(topic);
                      }
                      onClose();
                    }}
                    role="button"
                    tabIndex={0}
                    className="animate-catalog-card flex items-center space-x-3 bg-[#0d1524] hover:bg-[#111c30] p-3 rounded-xl border border-[#1b2b46] hover:border-[#c59b27]/60 cursor-pointer transition-all duration-200 group shadow-sm hover:shadow-md"
                  >
                    <img 
                      src={lawyer.photoUrl || "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=300"} 
                      alt={lawyer.fullName}
                      className="w-11 h-11 sm:w-12 sm:h-12 rounded-full object-cover border border-[#c59b27]/40 group-hover:border-[#c59b27] transition-colors shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h5 className="font-semibold text-xs sm:text-sm text-slate-100 group-hover:text-[#c59b27] transition-colors truncate">{lawyer.fullName}</h5>
                        <span className="text-[10px] text-[#c59b27] bg-[#c59b27]/10 px-2 py-0.5 rounded border border-[#c59b27]/20 group-hover:bg-[#c59b27] group-hover:text-slate-950 font-medium transition-colors ml-1.5 shrink-0">
                          {t('book_button', 'Qabul')} →
                        </span>
                      </div>
                      <p className="text-xs text-[#c59b27] truncate mt-0.5">{localizeLawyerPosition(lawyer.position || lawyer.title || 'Katta Advokat', currentLang)}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{lawyer.experienceYears} {t('lawyer_experience_suffix', 'yil tajriba')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer / CTAs */}
        <div className="px-4 sm:px-8 py-3.5 sm:py-5 bg-[#080d17] border-t border-[#1b2b46] flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 shrink-0">
          <div className="text-xs text-slate-400 flex items-center text-center sm:text-left">
            <Clock className="w-4 h-4 text-[#c59b27] mr-1.5 shrink-0" />
            <span className="truncate">{t('preliminary_review_time', 'Dastlabki yuridik ekspertiza: 24 soat ichida')}</span>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2.5 rounded-lg border border-slate-700 hover:border-slate-500 text-xs font-medium text-slate-300 transition-colors cursor-pointer"
            >
              {t('close_btn', 'Yopish')}
            </button>
            <button
              onClick={() => {
                onClose();
                if (onBookTopic) onBookTopic(topic);
              }}
              className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#c59b27] to-[#b38038] hover:from-[#d4af37] hover:to-[#c59b27] text-[#070d18] text-xs font-bold shadow-lg shadow-[#c59b27]/20 flex items-center justify-center transition-all cursor-pointer"
            >
              <Calendar className="w-4 h-4 mr-2 shrink-0" />
              <span>{t('book_in_this_field', 'Uchrashuv belgilash')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
